/* eslint-disable import/order */
/* eslint-disable import/no-extraneous-dependencies */
import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import axios from 'axios'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { getClientData } from '../../services/ClienteDados/getClientData'
import 'dayjs/locale/pt-br'
import {
  cancelarBaixa,
  getHistoricoBaixaDetails,
} from '../../services/HistoricoBaixaService/getHistoricoBaixa'
import { getProductAll } from '../../services/ProductService/getProducts'

dayjs.extend(customParseFormat)

export const usePredictionDetails = () => {
  const [historicoBaixaDetail, setHistoricoBaixaDetail] =
    useState<GetHistoryBaixaDetailType>({} as GetHistoryBaixaDetailType)
  const [clientData, setClientData] = useState<GetClientData>()

  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingHistory, setIsLoadingHistory] = useState(true)

  const [currentPageHistory, setCurrentPageHistory] = useState(1)
  const [currentPageSoldOff, setCurrentPageSoldOff] = useState(1)
  const [currentPagesHistoryBaixa, setCurrentHistoryBaixa] = useState(1)

  const [isCancelar, setIsCancelar] = useState(false)

  // Filtros da tela. Ha dois niveis de propósito: o "rascunho" (o que o usuario esta escolhendo) e
  // o "aplicado" (o que foi para a API). Sem isso, cada tecla no calendario dispararia uma busca.
  // Nada aqui vai para o localStorage: a rota e por cliente, e um filtro grudado da visita anterior
  // faria a tela do proximo cliente abrir recortada sem o usuario ter pedido.
  const [produtos, setProdutos] = useState<GetProductAll>([])
  // Guardamos IDS, nao nomes: o catalogo do tenant tem descricoes repetidas (mesmo nome, codigos
  // diferentes), e resolver nome -> id pelo primeiro que casar filtraria o produto errado sem
  // nenhum erro em log. Como o `value` da option passa a ser numerico, a tela precisa de
  // `optionFilterProp="label"` para a busca por palavra-chave continuar valendo.
  const [produtosSelecionados, setProdutosSelecionados] = useState<number[]>([])
  const [dataInicioRascunho, setDataInicioRascunho] = useState('')
  const [dataFimRascunho, setDataFimRascunho] = useState('')

  const [produtoIdAplicado, setProdutoIdAplicado] = useState<number[]>([])
  const [dataInicioAplicada, setDataInicioAplicada] = useState('')
  const [dataFimAplicada, setDataFimAplicada] = useState('')

  // Duas versoes, de proposito, e nao um contador so:
  // - `versaoConsulta` sobe a cada Aplicar/Limpar e e a DEPENDENCIA real do efeito de busca. Sem
  //   ela, "Limpar" com filtro ja limpo criaria arrays novos e refaria a busca a toa - e serializar
  //   os filtros no array de dependencias (o outro caminho) e proibido pelo baseline do projeto,
  //   ainda mais aqui, onde o .eslintrc nao carrega o plugin react-hooks para avisar de defasagem.
  // - `versaoLimpeza` sobe SO no Limpar e e a `key` dos dois calendarios. O DatesPicker do projeto
  //   e NAO-CONTROLADO (recebe `defaultValue`, nunca `value`), entao zerar o estado nao apaga a
  //   data escrita no campo: a tela seguiria exibindo "01/07/2026" sobre o historico completo, e
  //   um Aplicar seguinte nao filtraria nada, porque o input nunca reemite o valor. Trocar a key
  //   remonta o componente e limpa o campo. Somar as duas num contador so remontaria os
  //   calendarios tambem no Aplicar, apagando o filtro no instante em que ele e aplicado.
  const [versaoConsulta, setVersaoConsulta] = useState(0)
  const [versaoLimpeza, setVersaoLimpeza] = useState(0)

  const { id } = useParams()

  const getHistory = async (cancel?: any) => {
    setIsLoadingHistory(true)
    const result = await getHistoricoBaixaDetails(
      id!,
      currentPageHistory,
      produtoIdAplicado,
      dataInicioAplicada,
      dataFimAplicada,
      cancel
    )
    if ('message' in result) {
      // Requisicao abortada nao e erro: e a anterior sendo descartada porque o filtro mudou.
      // Sair antes de mexer no loading evita apagar o spinner da busca que ainda esta em voo.
      if (result.message === 'cancelToken') return
      setIsLoadingHistory(false)
      toast.error(result.message)
      return
    }
    setIsLoadingHistory(false)
    setHistoricoBaixaDetail(result)
  }

  const carregaProdutos = async () => {
    const result = await getProductAll('')
    if ('message' in result) {
      // Falha silenciosa aqui deixaria o Select vazio, e o usuario leria "este cliente nao tem
      // produtos" onde o certo e "a lista de produtos nao carregou".
      toast.error(result.message)
      return
    }
    setProdutos(result)
  }

  const opcoesProduto = useMemo(
    () => produtos.map(item => ({ value: item.id, label: item.nome })),
    [produtos]
  )

  const aplicarFiltros = () => {
    // dd/MM/yyyy invertido devolveria lista vazia, indistinguivel de "nao ha baixa no periodo".
    if (dataInicioRascunho && dataFimRascunho) {
      const inicio = dayjs(dataInicioRascunho, 'DD/MM/YYYY')
      const fim = dayjs(dataFimRascunho, 'DD/MM/YYYY')
      if (inicio.isValid() && fim.isValid() && inicio.isAfter(fim)) {
        toast.error('A data inicial não pode ser maior que a data final.')
        return
      }
    }

    setProdutoIdAplicado(produtosSelecionados)
    setDataInicioAplicada(dataInicioRascunho)
    setDataFimAplicada(dataFimRascunho)
    // Voltar para a primeira pagina e obrigatorio: o recorte novo pode ter menos paginas que o
    // anterior, e pedir a pagina 4 de um resultado com 2 traria uma tela vazia que parece erro.
    setCurrentPageHistory(1)
    setVersaoConsulta(versao => versao + 1)
  }

  const limparFiltros = () => {
    setProdutosSelecionados([])
    setDataInicioRascunho('')
    setDataFimRascunho('')
    setProdutoIdAplicado([])
    setDataInicioAplicada('')
    setDataFimAplicada('')
    setCurrentPageHistory(1)
    setVersaoConsulta(versao => versao + 1)
    setVersaoLimpeza(versao => versao + 1)
  }

  const temFiltroAplicado =
    produtoIdAplicado.length > 0 || !!dataInicioAplicada || !!dataFimAplicada

  const handleClientData = async () => {
    setIsLoading(true)
    const result = await getClientData(id!)
    setIsLoading(false)
    if ('message' in result) {
      toast.error(result.message)
    } else {
      setClientData(result)
    }
  }

  const cancelaUmaBaixa = async (idProduto: number, proximaCompra: string) => {
    const predicaoBaixaRequestDTO = {
      acertou: true,
      dataProximaCompra: proximaCompra,
      produtoId: idProduto,
    }

    const result = await cancelarBaixa(id, predicaoBaixaRequestDTO)

    if ('info' in result) {
      toast.success(result.info)
      // Volta para a primeira pagina em vez de recarregar a atual: cancelar o unico item da ultima
      // pagina deixaria `currentPageHistory` fora de faixa, com a tela vazia e a paginacao sumindo.
      // Com a pagina ja em 1, o contador de consulta e que dispara a busca.
      setCurrentPageHistory(1)
      setVersaoConsulta(versao => versao + 1)
      return
    }
    toast.error(result.message)
  }

  const showIsCancelar = () => setIsCancelar(!isCancelar)
  const [idCancela, setIdCancela] = useState(0)
  const [dataProx, setDataProx] = useState('')
  const isCancelarBaixa = () => cancelaUmaBaixa(idCancela, dataProx)

  // Refaz a busca quando muda a pagina OU o recorte aplicado (via `versaoConsulta`, que e uma
  // dependencia de verdade — nada de serializar filtros no array de deps). O cancelamento e
  // obrigatorio: a demanda multiplicou os gatilhos desta busca (Aplicar, Limpar, paginacao e
  // cancelamento de baixa), e sem ele duas respostas em voo podem chegar fora de ordem, deixando
  // a tela com o recorte da requisicao ANTERIOR.
  useEffect(() => {
    const source = axios.CancelToken.source()
    getHistory(source)
    return () => {
      source.cancel('cancelToken')
    }
  }, [currentPageHistory, versaoConsulta])

  useEffect(() => {
    handleClientData()
    carregaProdutos()
  }, [])

  return {
    historicoBaixaDetail,
    clientData,
    isLoading,
    isLoadingHistory,
    cancelaUmaBaixa,

    currentPagesHistoryBaixa,
    currentPageHistory,
    setCurrentPageHistory,
    currentPageSoldOff,
    setCurrentPageSoldOff,
    setCurrentHistoryBaixa,
    isCancelar,
    showIsCancelar,
    setIdCancela,
    setDataProx,
    isCancelarBaixa,

    opcoesProduto,
    produtosSelecionados,
    setProdutosSelecionados,
    setDataInicioRascunho,
    setDataFimRascunho,
    aplicarFiltros,
    limparFiltros,
    temFiltroAplicado,
    versaoLimpeza,
  }
}
